import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";

import { AppHeader } from "@/components/AppHeader";
import { Footer } from "@/components/Footer";
import { LoadInfoForm, CostSettingsForm } from "@/components/LoadForm";
import { MilesKpiRow, ProfitKpiGrid } from "@/components/KpiCards";
import { DecisionCard } from "@/components/DecisionCard";
import { HistoryTable } from "@/components/HistoryTable";
import { CompareDialog } from "@/components/CompareDialog";
import { PaywallDialog } from "@/components/PaywallDialog";
import { FuelAutoFill } from "@/components/FuelAutoFill";
import { AiAdvisorCard } from "@/components/AiAdvisorCard";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { FloppyDisk, FileText, ArrowCounterClockwise, ChartBar } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

import { DEFAULT_LOAD, computeMetrics, toLoadPayload } from "@/lib/defaults";
import { exportLoadPDF } from "@/lib/pdf";
import { useT } from "@/lib/i18n";
import { CALC } from "@/constants/testIds";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { isAuthenticated, isPro } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem("dpc_lang") || "en");
  const [load, setLoad] = useState(DEFAULT_LOAD);
  const [saved, setSaved] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareLoads, setCompareLoads] = useState([]);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [guestUsage, setGuestUsage] = useState(null);
  const [loadsLoading, setLoadsLoading] = useState(true);

  const t = useT(lang);

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    localStorage.setItem("dpc_lang", lang);
  }, [lang]);

  const m = useMemo(() => computeMetrics(load), [load]);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await api.get("/guest/usage");
      setGuestUsage(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchLoads = useCallback(async () => {
    try {
      const res = await api.get("/loads");
      setSaved(res.data || []);
    } catch {
      setSaved([]);
    } finally {
      setLoadsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
    fetchLoads();
  }, [fetchLoads, fetchUsage, isAuthenticated]);

  const handleSave = async () => {
    const payload = toLoadPayload(load);
    if (!payload.loaded_miles && !payload.rate) {
      toast.error(lang === "ar" ? "أدخل تفاصيل الحمولة قبل الحفظ" : "Enter load details before saving");
      return;
    }
    try {
      await api.post("/loads", payload);
      toast.success(t("savedSuccess"));
      fetchLoads();
      fetchUsage();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        setPaywallOpen(true);
        return;
      }
      toast.error(typeof detail === "string" ? detail : t("saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/loads/${id}`);
      toast.success(t("deleteSuccess"));
      fetchLoads();
    } catch {
      toast.error(t("deleteFailed"));
    }
  };

  const handleClearAll = async () => {
    if (!isAuthenticated) {
      toast.error("Sign in to manage your load history");
      return;
    }
    try {
      await api.delete("/loads");
      toast.success(t("deleteSuccess"));
      fetchLoads();
    } catch {
      toast.error(t("deleteFailed"));
    }
  };

  const handleExport = () => {
    exportLoadPDF(load, m, t);
    toast.success(t("exportSuccess"));
  };

  const handleExportRow = (l) => {
    const rowMetrics = {
      total_miles: l.total_miles,
      real_rpm: l.real_rpm,
      trip_cost: l.trip_cost,
      driver_pay: l.driver_pay,
      dispatcher_fee: l.dispatcher_fee,
      net_profit: l.net_profit,
      profit_per_mile: l.profit_per_mile,
      is_profitable: l.is_profitable,
    };
    exportLoadPDF(l, rowMetrics, t);
    toast.success(t("exportSuccess"));
  };

  const handleReset = () => setLoad(DEFAULT_LOAD);

  const focusCalculator = useCallback(() => {
    const el = document.querySelector('[data-testid="load-info-form"]') ||
               document.querySelector("form");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    const firstInput = el?.querySelector("input");
    firstInput?.focus?.();
  }, []);

  const handleCompare = (ids) => {
    if (ids.length !== 2) {
      toast.error(t("selectExactlyTwo"));
      return;
    }
    const picks = saved.filter((s) => ids.includes(s.id));
    setCompareLoads(picks);
    setCompareOpen(true);
  };

  const applyFuelCpm = (cpm) => {
    setLoad((p) => ({ ...p, cost_per_mile: cpm }));
  };

  return (
    <div className="App min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
      <AppHeader lang={lang} setLang={setLang} guestUsage={guestUsage} />

      <main className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        {!isAuthenticated && guestUsage && (
          <div
            data-testid="guest-banner"
            className={`rounded-xl border p-4 flex items-center justify-between gap-4 flex-wrap ${
              guestUsage.remaining > 0
                ? "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                : "border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-900"
            }`}
          >
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {guestUsage.remaining > 0
                  ? `You have ${guestUsage.remaining} of ${guestUsage.limit} free screenings left today.`
                  : "You've used all 3 free screenings today."}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Sign in to save history, run analytics and remove daily limits.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                data-testid="banner-login"
                className="text-xs px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                data-testid="banner-signup"
                className="btn-neon text-xs px-3 py-2 rounded-lg"
              >
                Sign up free
              </Link>
            </div>
          </div>
        )}

        {isAuthenticated && !isPro && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-medium text-slate-900">
                Unlock Pro — unlimited screenings, analytics and branded PDFs.
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                One lost load pays for 2+ years of the Pro plan.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/app/analytics"
                className="text-xs px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-white inline-flex items-center gap-1.5"
              >
                <ChartBar size={14} weight="bold" />
                Analytics
              </Link>
              <Link
                to="/app/billing"
                data-testid="banner-upgrade"
                className="btn-neon text-xs px-3 py-2 rounded-lg"
              >
                Upgrade $19/mo
              </Link>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 flex flex-col gap-6 animate-fade-up">
            <LoadInfoForm lang={lang} load={load} setLoad={setLoad} />

            <div className="relative">
              <div className="absolute end-4 top-4 z-10">
                <FuelAutoFill
                  pickCity={load.pick_city}
                  deliveryCity={load.delivery_city}
                  onApply={applyFuelCpm}
                />
              </div>
              <CostSettingsForm lang={lang} load={load} setLoad={setLoad} />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid={CALC.btnSave}
                onClick={handleSave}
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg h-10 px-4"
              >
                <FloppyDisk size={16} weight="bold" className="me-1.5" />
                {t("saveLoad")}
              </Button>
              <Button
                data-testid={CALC.btnExport}
                onClick={handleExport}
                variant="outline"
                className="rounded-lg h-10 px-4 border-slate-300"
              >
                <FileText size={16} weight="bold" className="me-1.5" />
                {t("exportPdf")}
              </Button>
              <Button
                data-testid={CALC.btnReset}
                onClick={handleReset}
                variant="ghost"
                className="rounded-lg h-10 px-4 text-slate-600 hover:text-slate-900"
              >
                <ArrowCounterClockwise size={16} weight="bold" className="me-1.5" />
                {t("resetDefaults")}
              </Button>
            </div>
          </div>

          <div className="lg:col-span-8 flex flex-col gap-6 animate-fade-up [animation-delay:90ms]">
            <section>
              <div className="flex items-center gap-2 mb-4 ps-1">
                <span className="w-2 h-2 rounded-full bg-blue-600" />
                <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-700 dark:text-slate-200">
                  {t("milesSummary")}
                </h2>
              </div>
              <MilesKpiRow lang={lang} load={load} m={m} />
            </section>

            <section>
              <div className="flex items-center gap-2 mb-4 ps-1">
                <span className="w-2 h-2 rounded-full bg-blue-600" />
                <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-slate-700 dark:text-slate-200">
                  {t("profitAnalysis")}
                </h2>
              </div>
              <ProfitKpiGrid lang={lang} load={load} m={m} />
            </section>

            <DecisionCard
          lang={lang}
          m={m}
          minGoal={load.min_profit_goal}
          isEmpty={!Number(load.rate) && !Number(load.loaded_miles)}
        />

            <AiAdvisorCard lang={lang} load={load} />
          </div>
        </div>

        <Separator className="bg-slate-200" />

        <HistoryTable
          lang={lang}
          loads={saved}
          loading={loadsLoading}
          onDelete={handleDelete}
          onClearAll={handleClearAll}
          onCompare={handleCompare}
          onExportRow={handleExportRow}
          onFocusCalculator={focusCalculator}
        />
      </main>

      <CompareDialog
        lang={lang}
        open={compareOpen}
        onOpenChange={setCompareOpen}
        loads={compareLoads}
      />

      <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} />

      <footer className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8 text-xs text-slate-400 tabular">
        LoadWise · Built for US dispatchers & drivers
      </footer>

      <Footer />

      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
